<template>
  <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    <div
      v-for="(item, index) in statsDataList"
      :key="index"
      class="bg-white p-4 rounded shadow-md flex flex-col items-center justify-center"
    >
      <!-- Custom Canvas Circular Progress -->
      <div class="relative w-32 h-32 flex items-center justify-center">
        <canvas
          :ref="(el) => setCanvasRef(el, index)"
          width="96"
          height="96"
          class="w-32 h-32"
        ></canvas>
        <!-- Value text in center -->
        <div class="absolute inset-0 flex items-center justify-center">
          <span class="text-lg font-bold text-gray-800">{{ item.value }}</span>
        </div>
      </div>
      <!-- Label below chart -->
      <div class="mt-3 text-center text-sm font-medium text-gray-700">
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, ref, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

const route = useRoute();

const activeTab = ref(route.fullPath);

const { t } = useI18n();

const props = defineProps({
  followStats: {
    type: Object,
    default: () => ({}),
  },
});

// Canvas refs
const canvasRefs = ref([]);

// Computed stats data for charts
const statsDataList = computed(() => {
  const stats = props.followStats;
  const totalUsers = stats.total_users || 0;

  return [
    /*{
      label: 'Total des utilisateurs',
      value: totalUsers,
      percentage: 100, // Always 100% for total
      color: '#00C2CB'
    },*/
    {
      label: t("user_progression.users_started_modules"),
      value: stats.users_started || 0,
      percentage:
        totalUsers > 0
          ? Math.round(((stats.users_started || 0) / totalUsers) * 100)
          : 0,
      color: "#66BB6A",
    },
    {
      label: t("user_progression.users_over_50"),
      value: stats.users_over_50 || 0,
      percentage:
        totalUsers > 0
          ? Math.round(((stats.users_over_50 || 0) / totalUsers) * 100)
          : 0,
      color: "#FFA726",
    },
    {
      label:
        activeTab.value === "/user-follow#pluzz"
          ? t("user_progression.users_less_50")
          : t("user_progression.users_completed_modules"),
      value: stats.users_completed || 0,
      percentage:
        totalUsers > 0
          ? Math.round(((stats.users_completed || 0) / totalUsers) * 100)
          : 0,
      color: "#EF5350",
    },
    {
      label:
        activeTab.value === "/user-follow#pluzz"
          ? t("user_progression.users_completed_modules")
          : t("user_progression.users_certified"),
      value: stats.users_certified || 0,
      percentage:
        totalUsers > 0
          ? Math.round(((stats.users_certified || 0) / totalUsers) * 100)
          : 0,
      color: "#26C6DA",
    },
  ];
});

// Canvas methods
const setCanvasRef = (el, index) => {
  if (el) {
    canvasRefs.value[index] = el;
    drawCircularProgress(el, statsDataList.value[index]);
  }
};

const drawCircularProgress = (canvas, data) => {
  const ctx = canvas.getContext("2d");
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const radius = 35;
  const lineWidth = 6;

  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw background circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
  ctx.strokeStyle = "#F3F4F6";
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  // Draw progress arc
  const startAngle = -Math.PI / 2; // Start from top
  const endAngle = startAngle + (2 * Math.PI * data.percentage) / 100;

  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, startAngle, endAngle);
  ctx.strokeStyle = data.color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.stroke();
};

// Watch for data changes to redraw charts
watch(
  statsDataList,
  (newData) => {
    newData.forEach((item, index) => {
      if (canvasRefs.value[index]) {
        drawCircularProgress(canvasRefs.value[index], item);
      }
    });
  },
  { deep: true }
);

// Redraw charts on mount
onMounted(() => {
  console.log(props.followStats);
  setTimeout(() => {
    statsDataList.value.forEach((item, index) => {
      if (canvasRefs.value[index]) {
        drawCircularProgress(canvasRefs.value[index], item);
      }
    });
  }, 100);
});
</script>

<style scoped>
.primary-text {
  color: #1f2937;
}
</style>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss" as *;
@use "../../assets/scss/app/common.scss" as *;
</style>
